import { clone } from "@/fp";
import { computed, Ref, ref } from "@vue/composition-api";

type SimpleMultiSelectionType = string | number;

type MultiSelectionResponse = [
  Readonly<
    Ref<
      readonly {
        val: SimpleMultiSelectionType;
        selected: boolean;
      }[]
    >
  >,
  (val: SimpleMultiSelectionType) => void,
  Readonly<Ref<readonly SimpleMultiSelectionType[]>>
];

export const UseMultiSelectionSimpleItems = (payload: {
  items: SimpleMultiSelectionType[];
  selected: SimpleMultiSelectionType[];
  always_keep_one_on: boolean;
}): MultiSelectionResponse => {
  const { always_keep_one_on, items, selected } = payload;

  const all_items: Ref<{
    val: SimpleMultiSelectionType;
    selected: boolean;
  }[]> = ref(
    [...items].map((d) => {
      return {
        val: d,
        selected: selected.includes(d),
      };
    })
  );

  const read_only_items = computed(() => all_items.value);
  const selected_values = computed(() =>
    all_items.value.filter((d) => d.selected).map((d) => d.val)
  );

  const check_if_toggled_item_should_stay_on = (
    val: SimpleMultiSelectionType
  ) => {
    return (
      always_keep_one_on &&
      selected_values.value.length === 1 &&
      selected_values.value[0] === val
    );
  };

  const toggle_item = (val: SimpleMultiSelectionType) => {
    if (check_if_toggled_item_should_stay_on(val)) {
      return;
    }
    all_items.value = all_items.value.map((d) => {
      if (d.val === val) {
        return {
          val,
          selected: !d.selected,
        };
      }
      return d;
    });
  };

  return [read_only_items, toggle_item, selected_values];
};

export type PendingMultiSelectionResponse = {
  items: Ref<
    {
      val: SimpleMultiSelectionType;
      selected: boolean;
    }[]
  >;
  selected: Readonly<Ref<readonly SimpleMultiSelectionType[]>>;
  discard: () => void;
  submit: () => void;
  toggle_item: (item: SimpleMultiSelectionType) => void;
};

export const UsePendingMultiSelectionSimpleItems = (payload: {
  items: SimpleMultiSelectionType[];
  selected: SimpleMultiSelectionType[];
  always_keep_one_on: boolean;
}): PendingMultiSelectionResponse => {
  const { always_keep_one_on, items, selected } = payload;

  const items_before_change: Ref<{
    val: SimpleMultiSelectionType;
    selected: boolean;
  }[]> = ref(
    [...items].map((d) => {
      return {
        val: d,
        id: d,
        checked: selected.includes(d),
        selected: selected.includes(d),
      };
    })
  );

  //const read_only_items = computed(() => all_items.value);
  const pending_items = ref(clone(items_before_change.value, true));

  const selected_values = computed(() =>
    items_before_change.value.filter((d) => d.selected).map((d) => d.val)
  );

  const check_if_toggled_item_should_stay_on = (
    val: SimpleMultiSelectionType
  ) => {
    return (
      always_keep_one_on &&
      selected_values.value.length === 1 &&
      selected_values.value[0] === val
    );
  };

  const discard = () => {
    pending_items.value = clone(items_before_change.value, true);
  };
  const submit = () => {
    items_before_change.value = clone(pending_items.value, true);
  };

  const toggle_item = (val: SimpleMultiSelectionType) => {
    if (check_if_toggled_item_should_stay_on(val)) {
      return;
    }
    pending_items.value = pending_items.value.map((d) => {
      if (d.val === val) {
        return {
          ...d,
          checked: !d.selected,
          selected: !d.selected,
        };
      }
      return d;
    });
  };

  return {
    items: pending_items,
    discard,
    submit,
    toggle_item,
    selected: selected_values,
  };
};
